import { Component, OnInit } from '@angular/core';
import { Directions, FluidFlowModelListItemDto, SimulationResponse, SimulationResults } from '../../../../core/models/simulation.model';
import { Destroyable } from '../../../../utils/mixins/destroyable.mixin';

import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { SimulatorType } from '../../../../core/enums/simulator-type.enum';
import { Router } from '@angular/router';
import { SimulationsService } from '../../../../services/simulations/simulations.service';
import { SnackbarService } from '../../../../services/snackbar.service';
import { LoadingService } from '../../../../services/loading.service';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import { COMPONENT_ID_HEADER } from '../../../../core/consts/api.const';
import { MatDialog } from '@angular/material/dialog';
import { SelectUpscalingRockImagePopupComponent } from '../../../../common/popups/select-upscaling-rock-image-popup/select-upscaling-rock-image-popup.component';
import { SelectRockModelPopupComponent } from '../../../../common/popups/select-rock-model-popup/select-rock-model-popup.component';
import { SelectFluidModelPopupComponent } from '../../../../common/popups/select-fluid-model-popup/select-fluid-model-popup.component';
import { filter, forkJoin, of, switchMap } from 'rxjs';
import { UpscalingRockFile } from '../../../../core/models/upscaling-rock-files.model';
import { RockModel } from '../../../../core/models/rock-model.model';
import { RemoveExtensionPipe } from '../../../../common/pipes/remove-extension.pipe';
import { RockThumbnailService } from '../../../../services/rock-thumbnail/rock-thumbnail.service';
import { RockImagesService } from '../../../../services/rock-images/rock-images.service';
import { RockFileLoader } from '../../../../utils/rock-file-loader.util';
import { RockModelsService } from '../../../../services/rock-models/rock-models.service';
import { RockPreviewComponent } from '../../../../common/rock-preview/rock-preview.component';

@Component({
  selector: 'app-upscaling-capillary-pressure',
  imports: [
    FormsModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSnackBarModule,
    MatRadioModule,
    ReactiveFormsModule,
    RockPreviewComponent
],
  templateUrl: './capillary-pressure.component.html',
  providers: [RemoveExtensionPipe],
  styleUrl: './capillary-pressure.component.scss',
  standalone: true
})
export class UpscalingCapillaryPressureComponent extends Destroyable(Object) implements OnInit {
  simulationDirectionsForm!: FormGroup;
  simulationNameForm!: FormGroup;
  fileName!: FormControl;
  rockModelControl!: FormControl;
  fluidModelControl!: FormControl;
  isLoading: boolean = false;
  thumbnailUrl: string | null = null;
  rockFileMetadata: UpscalingRockFile | null = null;
  availableRockModels: RockModel[] = [];
  availableFluidModels: FluidFlowModelListItemDto[] = [];
  isLoadingRockModels: boolean = false;
  isLoadingFluidModels: boolean = false;
  
  private rockFileLoader: RockFileLoader;

  readonly config = {
    title: 'Upscaling - Capillary Pressure',
    simulatorType: SimulatorType.UpscalingPC,
  };

  constructor(
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly dialog: MatDialog,
    private readonly simulationsService: SimulationsService,
    private readonly snackbarService: SnackbarService,
    private readonly loadingService: LoadingService,
    private readonly removeExtensionPipe: RemoveExtensionPipe,
    private readonly rockThumbnailService: RockThumbnailService,
    private readonly rockImagesService: RockImagesService,
    private readonly rockModelsService: RockModelsService,
  ) {
    super();
    this.rockFileLoader = new RockFileLoader(rockThumbnailService, rockImagesService);
  }

  get totalPorosity(): string {
    const selectedModel = this.rockModelControl?.value as RockModel | null;
    if (selectedModel && selectedModel.totalPorosity != null) {
      return selectedModel.totalPorosity.toFixed(2);
    }
    return 'N/A';
  }

  get shouldShowPorosity(): boolean {
    return !!this.rockModelControl?.value;
  }

  get componentId(): SimulatorType {
    return this.config.simulatorType;
  }

  get nameControl(): FormControl {
    return this.simulationNameForm.get('name') as FormControl;
  }

  get rockModelName(): string {
    const selectedModel = this.rockModelControl.value as RockModel | null;
    return selectedModel ? selectedModel.name : '';
  }

  get fluidModelName(): string {
    const selectedModel = this.fluidModelControl.value as FluidFlowModelListItemDto | null;
    return selectedModel ? selectedModel.name : '';
  }

  get selectedDirection(): FormControl {
    return this.simulationDirectionsForm.get('selectedDirection') as FormControl;
  }

  get simulationDirections(): Directions {
    return {
      xDirection: this.selectedDirection?.value === 'xDirection',
      yDirection: this.selectedDirection?.value === 'yDirection',
      zDirection: this.selectedDirection?.value === 'zDirection',
    };
  }

  get selectedCycles(): string {
    const selectedModel = this.fluidModelControl.value as FluidFlowModelListItemDto | null;
    if (!selectedModel) {
      return '';
    }
    
    const cycles: string[] = [];
    
    // Check if primaryDrainage is available
    if (selectedModel.primaryDrainage && 
        (selectedModel.primaryDrainage.min !== 0 || selectedModel.primaryDrainage.max !== 0)) {
      cycles.push('PD');
    }
    
    // Check if firstImbibition is available
    if (selectedModel.firstImbibition && 
        (selectedModel.firstImbibition.min !== 0 || selectedModel.firstImbibition.max !== 0)) {
      cycles.push('FI');
    }
    
    return cycles.join('/');
  }

  ngOnInit(): void {
    this.initSimulationNameForm();
    this.initSimulationDirectionsForm();
    this.initFileName();
    this.initRockModelControl();
    this.initFluidModelControl();
    this.subscribeToLoadingState();
  }

  selectRockFileName(): void {
    const dialogRef = this.dialog.open(SelectUpscalingRockImagePopupComponent, {
      panelClass: 'large-popup-size'
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((file: UpscalingRockFile) => {
        this.fileName.setValue(this.removeExtensionPipe.transform(file.fileName));
        this.rockFileMetadata = file;
        this.loadThumbnail(file.fileName);
        this.updateAvailableRockModels(file);
      });
  }

  selectRockModel(): void {
    if (!this.fileName.value) {
      this.snackbarService.openCustom({
        message: 'Please select an upscaling image first',
        title: 'Warning',
        type: 'warning',
        action: 'Close',
      });
      return;
    }

    const dialogRef = this.dialog.open(SelectRockModelPopupComponent, {
      panelClass: 'large-popup-size',
      data: { rockModels: this.availableRockModels }
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((model: RockModel) => {
        this.rockModelControl.setValue(model);
        this.updateAvailableFluidModels(model);
      });
  }

  selectFluidModel(): void {
    if (!this.rockModelControl.value) {
      this.snackbarService.openCustom({
        message: 'Please select a rock model first',
        title: 'Warning',
        type: 'warning',
        action: 'Close',
      });
      return;
    }

    const dialogRef = this.dialog.open(SelectFluidModelPopupComponent, {
      panelClass: 'large-popup-size',
      data: { fluidModels: this.availableFluidModels }
    });

    dialogRef.afterClosed()
      .pipe(
        filter(Boolean),
        this.takeUntilDestroyed()
      )
      .subscribe((model: FluidFlowModelListItemDto) => {
        this.fluidModelControl.setValue(model);
      });
  }

  private updateAvailableRockModels(file: UpscalingRockFile): void {
    // Reset rock models and disable control while loading
    this.availableRockModels = [];
    this.rockModelControl.setValue(null);
    this.rockModelControl.disable();
    this.isLoadingRockModels = true;
    
    // Reset fluid models as well
    this.availableFluidModels = [];
    this.fluidModelControl.setValue(null);
    this.fluidModelControl.disable();
    
    // Fetch rock models from the API
    const options = {
      headers: new HttpHeaders({
        [COMPONENT_ID_HEADER]: this.componentId,
      }),
    };
    
    this.rockModelsService
      .getUpscalingRockFileModels(file.fileName, options)
      .pipe(this.takeUntilDestroyed())
      .subscribe({
        next: (rockModels) => {
          // Map UpscalingRockFileModelShortDto[] to RockModel[]
          this.availableRockModels = rockModels.map(model => ({
            id: model.id,
            name: model.name,
            rockFileName: model.rockFileName,
            totalPorosity: model.totalPorosity,
            permeability: model.permeability ? 'Selected' : 'Not selected',
            formationFactor: model.formationFactor ? 'Selected' : 'Not selected',
          }));
          
          this.isLoadingRockModels = false;
          
          // Enable control if rock models are available
          if (this.availableRockModels.length > 0) {
            this.rockModelControl.enable();
          } else {
            // Show notification when no rock models are found
            this.snackbarService.openCustom({
              message: 'No rock models found for this upscaling image.',
              title: 'Info',
              type: 'info',
              action: 'Close',
            });
          }
        },
        error: (error) => {
          this.isLoadingRockModels = false;
          this.snackbarService.openCustom({
            message: 'Failed to load rock models for the selected file',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
          console.error('Error loading rock models:', error);
        }
      });
  }

  private updateAvailableFluidModels(rockModel: RockModel): void {
    // Reset fluid models and disable control while loading
    this.availableFluidModels = [];
    this.fluidModelControl.setValue(null);
    this.fluidModelControl.disable();
    this.isLoadingFluidModels = true;
    
    // Search for UpscalingKabs simulations that used the selected rock model
    // We search by rock file name (not the rock model name)
    const searchFilters = {
      filterType: 'File',
      searchString: rockModel.rockFileName || rockModel.name,
      types: [SimulatorType.UpscalingKabs],
      runStatus: ['Success'],
      directions: {
        x: true,
        y: true,
        z: true,
      },
      page: 1,
      pageSize: 100, // Get a reasonable number of simulations
      startDate: null,
      endDate: null,
    };

    this.simulationsService
      .getFilteredSimulations(searchFilters, false)
      .pipe(
        switchMap((simulationResults: any) => {
          // Extract simulations from the results
          const simulations = simulationResults.data;
          
          if (!simulations || simulations.length === 0) {
            // Return an observable that emits empty array so subscriber is notified
            return of([]);
          }

          // For each simulation, fetch its fluid flow models
          const fluidModelRequests = simulations.map((sim: any) =>
            this.simulationsService.getFluidFlowModels(sim.id)
          );

          return forkJoin(fluidModelRequests);
        }),
        this.takeUntilDestroyed()
      )
      .subscribe({
        next: (fluidModelArrays: any) => {
          // Handle empty result - when switchMap returns []
          if (!Array.isArray(fluidModelArrays) || fluidModelArrays.length === 0) {
            this.availableFluidModels = [];
            this.isLoadingFluidModels = false;
            this.snackbarService.openCustom({
              message: 'No fluid flow models found for this rock model.',
              title: 'Info',
              type: 'info',
              action: 'Close',
            });
            return;
          }

          // Flatten the array of arrays and remove duplicates based on ID
          const allFluidModels = fluidModelArrays.flat() as FluidFlowModelListItemDto[];
          
          // Filter out any null/undefined values
          const validFluidModels = allFluidModels.filter(model => model && model.id);
          
          const uniqueFluidModels = Array.from(
            new Map(validFluidModels.map((model: FluidFlowModelListItemDto) => [model.id, model])).values()
          );
          
          this.availableFluidModels = uniqueFluidModels;
          this.isLoadingFluidModels = false;
          
          // Enable control if fluid models are available
          if (this.availableFluidModels.length > 0) {
            this.fluidModelControl.enable();
          } else {
            // Show notification when there are simulations but no fluid models
            this.snackbarService.openCustom({
              message: 'No fluid flow models found for this rock model.',
              title: 'Info',
              type: 'info',
              action: 'Close',
            });
          }
        },
        error: (error) => {
          this.isLoadingFluidModels = false;
          this.snackbarService.openCustom({
            message: 'Failed to load fluid flow models',
            title: 'Error',
            type: 'error',
            action: 'Close',
          });
          console.error('Error loading fluid models:', error);
        }
      });
  }

  private loadThumbnail(fileName: string): void {
    const previousThumbnailUrl = this.thumbnailUrl;

    // Load thumbnail for upscaling image type
    this.rockFileLoader
      .getThumbnail(fileName, 'Upscaling')
      .pipe(this.takeUntilDestroyed())
      .subscribe((url) => {
        if (url) {
          if (previousThumbnailUrl) {
            this.rockFileLoader.revokeThumbnailUrl(previousThumbnailUrl);
          }
          this.thumbnailUrl = url;
        }
      });
  }

  override ngOnDestroy(): void {
    if (this.thumbnailUrl) {
      this.rockFileLoader.revokeThumbnailUrl(this.thumbnailUrl);
    }
    super.ngOnDestroy();
  }

  runSimulation(): void {
    if (this.isSimulationInputValid()) {
      const directions: Directions = this.simulationDirections;
      const selectedRockModel = this.rockModelControl.value as RockModel;
      const selectedFluidModel = this.fluidModelControl.value as FluidFlowModelListItemDto;
      
      let params = new HttpParams().set('name', this.getSimulationName());
      
      // Add parent simulation ID (the UpscalingKabs simulation ID)
      if (selectedFluidModel?.upscalingSimulationId) {
        params = params.set('ParentSimulationId', selectedFluidModel.upscalingSimulationId);
      }
      
      // Add rock model ID and name if available
      if (selectedRockModel?.id) {
        params = params.set('RockFileModelId', selectedRockModel.id);
      }
      if (selectedRockModel?.name) {
        params = params.set('RockFileModelName', selectedRockModel.name);
      }
      
      // Add fluid flow model ID and name
      if (selectedFluidModel?.id) {
        params = params.set('FluidFlowModelId', selectedFluidModel.id);
      }
      if (selectedFluidModel?.name) {
        params = params.set('FluidFlowModelName', selectedFluidModel.name);
      }
      
      const options = {
        headers: new HttpHeaders({
          [COMPONENT_ID_HEADER]: this.componentId,
        }),
        params: params,
      };

      this.loadingService.startLoading(this.componentId);

      this.simulationsService
        .addSimulation(directions, this.fileName.getRawValue(), options, this.componentId)
        .pipe(this.takeUntilDestroyed())
        .subscribe(() => {
          this.snackbarService.openCustom({
            message: 'Simulation started successfully',
            title: 'Success',
            type: 'success',
            action: 'Close',
          });
          this.fileName.patchValue('');
          this.router.navigate(['/dashboard']);
        });
    }
  }

  directionSelected(): boolean {
    return Boolean(this.selectedDirection?.value);
  }

  isSimulationInputValid(): boolean {
    return Boolean(
      this.fileName.getRawValue() &&
      this.simulationNameForm.valid &&
      this.directionSelected() &&
      this.rockModelControl.value &&
      this.fluidModelControl.value,
    );
  }

  private getSimulationName(): string {
    const { name } = this.simulationNameForm.getRawValue();
    return name || (this.fileName.getRawValue() ?? '').split('.')[0];
  }

  private initSimulationNameForm(): void {
    this.simulationNameForm = this.fb.group({
      name: ['', Validators.maxLength(256)],
    });
  }

  private initSimulationDirectionsForm(): void {
    this.simulationDirectionsForm = this.fb.group({
      selectedDirection: ['xDirection'],
    });
  }

  private initFileName(): void {
    this.fileName = new FormControl({ value: '', disabled: true });
  }

  private initRockModelControl(): void {
    this.rockModelControl = new FormControl({ value: null, disabled: true });
  }

  private initFluidModelControl(): void {
    this.fluidModelControl = new FormControl({ value: null, disabled: true });
  }

  private subscribeToLoadingState(): void {
    this.loadingService
      .getLoadingState(this.componentId)
      .pipe(this.takeUntilDestroyed())
      .subscribe((loadingState: boolean) => {
        this.isLoading = loadingState;
      });
  }
}
